import { LayoutModule } from "@angular/cdk/layout";
import { CommonModule } from "@angular/common";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, enableProdMode, importProvidersFrom } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { CountryListResponse } from "@models/country.model";
import { FlexLayoutModule } from "@ngbracket/ngx-layout";
import { AppStateService, RequestService } from "@services/index";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { NgxPaginationModule } from "ngx-pagination";
import { firstValueFrom } from "rxjs";
import { AppComponent } from "./app/app.component";
import { AppRoutingModule } from "./app/app.routes";
import { httpInterceptorProviders } from "./app/interceptors";
import { CustomFirebaseModule } from "./app/modules/custom-firebase/custom-firebase.module";
import { environment } from "./environments/environment";

if (environment.production) {
  enableProdMode();
}
function initializeAppFactory(requestService: RequestService, appStateService: AppStateService) {
  return async () => {
    // Establish auth state from the session cookie before the app renders (replaces the
    // old localStorage-token bootstrap). Failure just means "not logged in" — AuthGaurd
    // will redirect to /login as needed, so don't block app startup on it.
    try {
      await firstValueFrom(appStateService.bootstrapSession());
    } catch (error) {
      console.error("Error bootstrapping session:", error);
    }

    try {
      // Get countries
      const countriesRes = await firstValueFrom(
        requestService.get<CountryListResponse>("/api/v2/country")
      );
      if (countriesRes.status === true) {
        appStateService.allCountries.set(countriesRes.data);
      }
    } catch (error) {
      console.error("Error initializing app:", error);
    }
  };
}
bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      MatIconModule,
      RouterModule,
      AngularEditorModule,
      MatProgressSpinnerModule,
      LayoutModule,
      MatToolbarModule,
      MatButtonModule,
      MatSidenavModule,
      MatListModule,
      FlexLayoutModule,
      AppRoutingModule,
      CustomFirebaseModule,
      NgxPaginationModule
    ),
    httpInterceptorProviders,
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: "outline", floatLabel: "always" },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [RequestService, AppStateService],
      multi: true,
    },
  ],
});
